import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  RefObject,
} from 'react';

/**
 * Movable modals (MPN-24). Staff open a form over a detail page and still need
 * to read the card or ledger the popup now covers. Every popup renders through
 * `Modal`, so the placement logic lives here, in one hook, and the two
 * self-rolled dialogs reuse it.
 *
 * Model: the panel stays centred by the flex wrapper and gets a `translate`
 * offset on top. Dragging the handle (the header) sets a free offset, the dock
 * buttons snap it to a viewport edge, and the offset is clamped so the panel
 * can never leave the viewport. Once moved, the modal is "aside": the scrim
 * lightens so the page is readable, clicking the scrim no longer closes the
 * form, and page scroll unlocks so the wheel over the scrim scrolls the page —
 * but the scrim still swallows clicks, so nothing behind can change while a
 * form is open. Placement is remembered per title for the session, so a
 * repetitive task keeps its modal where the operator parked it.
 */

export type DockSide = 'left' | 'right';

export interface ModalPlacement {
  /** Offset from the centred position, in CSS px. */
  x: number;
  y: number;
  /** The edge the panel is docked to; null when centred or free-dragged. */
  dock: DockSide | null;
}

export const CENTRED_PLACEMENT: ModalPlacement = { x: 0, y: 0, dock: null };

/** Gap kept between a docked panel and the viewport edge (matches the panel's mx-4). */
export const DOCK_EDGE_GAP = 16;

/** Marks the element(s) a modal can be dragged by. The built-in header carries it. */
export const MODAL_DRAG_HANDLE_ATTR = 'data-modal-drag-handle';

/** Scrim over the page while the modal sits centred (Figma "Drop Pop Up" 30%). */
export const SCRIM_COLOR = 'rgba(0, 0, 0, 0.3)';
/** Scrim once the modal has been moved aside — the page behind must stay readable. */
export const MOVED_SCRIM_COLOR = 'rgba(0, 0, 0, 0.08)';

const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, [role="button"], [role="tab"]';

/** Session-scoped memory: where each modal (keyed by title) was last left. */
const placementMemory = new Map<string, ModalPlacement>();

/** Test hook — forgets every remembered placement. */
export function resetModalPlacements() {
  placementMemory.clear();
}

export function isMovedPlacement(p: ModalPlacement): boolean {
  return p.x !== 0 || p.y !== 0 || p.dock !== null;
}

interface BaseRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The panel's rect with the current translate removed — where flex centres it. */
function baseRect(panel: HTMLElement, current: ModalPlacement): BaseRect {
  const rect = panel.getBoundingClientRect();
  return {
    left: rect.left - current.x,
    top: rect.top - current.y,
    width: rect.width,
    height: rect.height,
  };
}

function clampAxis(value: number, min: number, max: number): number {
  // A panel larger than the viewport on this axis stays where flex put it.
  if (max < min) return 0;
  return Math.min(Math.max(value, min), max);
}

/** Keeps the whole panel inside the viewport so the handle stays reachable. */
function clampOffset(base: BaseRect, x: number, y: number) {
  return {
    x: clampAxis(x, -base.left, window.innerWidth - base.left - base.width),
    y: clampAxis(y, -base.top, window.innerHeight - base.top - base.height),
  };
}

function dockOffset(base: BaseRect, side: DockSide) {
  const x =
    side === 'left'
      ? DOCK_EDGE_GAP - base.left
      : window.innerWidth - DOCK_EDGE_GAP - base.left - base.width;
  return clampOffset(base, x, 0);
}

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  base: BaseRect;
}

export interface UseModalPlacementOptions {
  isOpen: boolean;
  /** Remembers the placement for the session under this key (the modal title). */
  memoryKey?: string;
  /** false pins the modal centred: no drag, no dock, no memory. */
  enabled?: boolean;
}

export interface ModalPanelHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

export function useModalPlacement({
  isOpen,
  memoryKey,
  enabled = true,
}: UseModalPlacementOptions) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacementState] =
    useState<ModalPlacement>(CENTRED_PLACEMENT);
  const placementRef = useRef<ModalPlacement>(CENTRED_PLACEMENT);
  const dragRef = useRef<DragState | null>(null);
  const [dragging, setDragging] = useState(false);
  const wasOpenRef = useRef(false);

  const setPlacement = useCallback(
    (next: ModalPlacement) => {
      placementRef.current = next;
      setPlacementState(next);
      if (memoryKey) placementMemory.set(memoryKey, next);
    },
    [memoryKey]
  );

  // Close → drop the live offset (memory keeps it) so the next open renders
  // centred and measures from a clean rect. Open → restore what the session
  // remembers for this title, recomputed against the current panel size and
  // viewport.
  useLayoutEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      dragRef.current = null;
      if (placementRef.current !== CENTRED_PLACEMENT) {
        placementRef.current = CENTRED_PLACEMENT;
        setPlacementState(CENTRED_PLACEMENT);
      }
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;

    const remembered =
      enabled && memoryKey ? placementMemory.get(memoryKey) : undefined;
    const panel = panelRef.current;
    if (!remembered || !isMovedPlacement(remembered) || !panel) return;

    const base = baseRect(panel, CENTRED_PLACEMENT);
    setPlacement(
      remembered.dock
        ? { ...dockOffset(base, remembered.dock), dock: remembered.dock }
        : { ...clampOffset(base, remembered.x, remembered.y), dock: null }
    );
  }, [isOpen, enabled, memoryKey, setPlacement]);

  // Viewport resize: a docked panel follows its edge, a dragged one stays inside.
  useEffect(() => {
    if (!isOpen || !enabled) return;
    const onResize = () => {
      const panel = panelRef.current;
      const current = placementRef.current;
      if (!panel || !isMovedPlacement(current)) return;
      const base = baseRect(panel, current);
      const next = current.dock
        ? dockOffset(base, current.dock)
        : clampOffset(base, current.x, current.y);
      if (next.x !== current.x || next.y !== current.y) {
        setPlacement({ ...current, ...next });
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, enabled, setPlacement]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled || event.button !== 0) return;
      const target = event.target as Element;
      if (
        !target.closest(`[${MODAL_DRAG_HANDLE_ATTR}]`) ||
        target.closest(INTERACTIVE_SELECTOR)
      ) {
        return;
      }
      const panel = panelRef.current;
      if (!panel) return;
      const current = placementRef.current;
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: current.x,
        originY: current.y,
        base: baseRect(panel, current),
      };
      capturePointer(event.currentTarget, event.pointerId);
      setDragging(true);
      // No text selection / focus jump from grabbing the header.
      event.preventDefault();
    },
    [enabled]
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const { x, y } = clampOffset(
        drag.base,
        drag.originX + event.clientX - drag.startX,
        drag.originY + event.clientY - drag.startY
      );
      const current = placementRef.current;
      if (x === current.x && y === current.y) return;
      setPlacement({ x, y, dock: null });
    },
    [setPlacement]
  );

  const endDrag = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    releasePointer(event.currentTarget, event.pointerId);
    setDragging(false);
  }, []);

  /** Snaps to an edge; pressing the active edge again re-centres. */
  const dock = useCallback(
    (side: DockSide) => {
      const panel = panelRef.current;
      if (!panel || !enabled) return;
      const current = placementRef.current;
      if (current.dock === side) {
        setPlacement(CENTRED_PLACEMENT);
        return;
      }
      setPlacement({
        ...dockOffset(baseRect(panel, current), side),
        dock: side,
      });
    },
    [enabled, setPlacement]
  );

  const recentre = useCallback(() => {
    setPlacement(CENTRED_PLACEMENT);
  }, [setPlacement]);

  const moved = enabled && isMovedPlacement(placement);
  const panelStyle: CSSProperties | undefined = moved
    ? { transform: `translate(${placement.x}px, ${placement.y}px)` }
    : undefined;

  const panelHandlers: ModalPanelHandlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  };

  return {
    panelRef,
    placement,
    moved,
    dragging,
    panelStyle,
    panelHandlers,
    dock,
    recentre,
  };
}

// jsdom has no pointer capture; browsers do, and it keeps a fast drag attached
// to the panel even when the pointer leaves it.
function capturePointer(element: Element, pointerId: number) {
  try {
    element.setPointerCapture?.(pointerId);
  } catch {
    /* not supported */
  }
}

function releasePointer(element: Element, pointerId: number) {
  try {
    if (element.hasPointerCapture?.(pointerId)) {
      element.releasePointerCapture(pointerId);
    }
  } catch {
    /* not supported */
  }
}

/**
 * Locks page scroll while `locked`. A centred modal locks it (as before); a
 * moved modal releases it so the wheel over the scrim scrolls the page behind.
 */
export function useBodyScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [locked]);
}

/**
 * While the page behind is scrollable, a wheel over the panel must not chain
 * to it: a form whose content fits would otherwise scroll the page under the
 * operator's pointer. Inner scroll areas (content, textareas) keep scrolling
 * until they hit their end, then stop rather than hand off.
 */
export function useWheelGuard(
  panelRef: RefObject<HTMLElement | null>,
  active: boolean
) {
  useEffect(() => {
    const panel = panelRef.current;
    if (!active || !panel) return;
    const onWheel = (event: WheelEvent) => {
      if (innerScrollerTakes(event, panel)) return;
      event.preventDefault();
    };
    panel.addEventListener('wheel', onWheel, { passive: false });
    return () => panel.removeEventListener('wheel', onWheel);
  }, [panelRef, active]);
}

function innerScrollerTakes(event: WheelEvent, panel: HTMLElement): boolean {
  if (event.deltaY === 0) return true;
  let el = event.target instanceof HTMLElement ? event.target : null;
  while (el) {
    if (
      el.scrollHeight > el.clientHeight &&
      /(auto|scroll)/.test(getComputedStyle(el).overflowY)
    ) {
      const goingUp = event.deltaY < 0;
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      if ((goingUp && !atTop) || (!goingUp && !atBottom)) return true;
    }
    if (el === panel) break;
    el = el.parentElement;
  }
  return false;
}
